import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { api } from '../data/api'
import { useAuth } from '../hooks'

const TRIAL_LEVELS_ORDER = ['Beginner', 'Intermediate', 'Advanced', 'Expert']

// Reached three ways: an org drilled into one specific examinee's History
// (location.state carries userId), an org's own org-wide Trial Attempts tile
// (no userId, role organisation/superadmin — covers every examinee at once),
// or an examinee's own History page viewing their own Trials (no userId,
// role user).
export default function TrialAttemptsPage() {
  const location = useLocation()
  const navigate  = useNavigate()
  const { user }  = useAuth()
  const viewUser  = location.state?.userId ? { id: location.state.userId, name: location.state.userName } : null
  const isSelf    = !viewUser && user?.role === 'user'
  // Per-exam layout (exam name + 4 level columns, no examinee column/search)
  // covers both "org looking at one examinee" and "examinee looking at
  // themselves" — same shape either way, just a different subject id.
  const perExam   = !!(viewUser || isSelf)
  const subjectId = viewUser?.id ?? (isSelf ? (user?._id ?? user?.id) : null)
  const orgId     = user?._id ?? user?.id

  const [summary, setSummary] = useState(null)
  const [search,  setSearch]  = useState('')

  useEffect(() => {
    const endpoint = perExam
      ? `/api/users/${subjectId}/trial-summary`
      : `/api/users/trial-summary/${orgId}`
    if (perExam ? !subjectId : !orgId) { setSummary([]); return }
    api.get(endpoint)
      .then(setSummary)
      .catch(() => setSummary([]))
  }, [perExam, subjectId, orgId])

  // Only rows with at least one real attempt somewhere — an exam (or
  // examinee+exam pair) sitting at all-zero was never actually tried, so
  // it's noise here, not signal.
  const attempted = (summary ?? []).filter(({ levels }) => Object.values(levels).some(n => n > 0))
  const searchLower = search.trim().toLowerCase()
  const filtered = !perExam && searchLower
    ? attempted.filter(r => r.userName?.toLowerCase().includes(searchLower) || r.examName.toLowerCase().includes(searchLower))
    : attempted

  return (
    <div className="px-8 sm:px-40 py-8 sm:py-12">
      <div className="mb-8">
        <button onClick={() => navigate('/history', viewUser ? { state: { userId: viewUser.id, userName: viewUser.name } } : undefined)}
          className="text-xs font-medium mb-4 flex items-center gap-1 transition"
          style={{ color: 'var(--text-3)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--text-1)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-3)'}
        >← Back to History</button>
        {viewUser && <p className="text-xs font-semibold uppercase tracking-widest mb-2" style={{ color: 'var(--brand)' }}>{viewUser.name}</p>}
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2" style={{ color: 'var(--text-1)' }}>Trial Attempts</h1>
        <p className="text-sm" style={{ color: 'var(--text-3)' }}>
          {perExam ? 'How many times each Trial difficulty level has been attempted, per exam.' : 'Every examinee’s Trial attempts, per exam and difficulty level.'}
        </p>
      </div>

      {!perExam && attempted.length > 0 && (
        <div className="mb-4 flex items-center gap-3">
          <div className="relative w-56">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm" style={{ color: 'var(--text-3)' }}>🔍</span>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search..."
              className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none transition-all"
              style={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-1)' }}
              onFocus={e => e.currentTarget.style.borderColor = 'var(--brand)'}
              onBlur={e  => e.currentTarget.style.borderColor = 'var(--border)'}
            />
            {search && (
              <button onClick={() => setSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs"
                      style={{ color: 'var(--text-3)' }}>✕</button>
            )}
          </div>
          <span className="text-xs" style={{ color: 'var(--text-3)' }}>{filtered.length} result{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      )}

      {summary === null ? (
        <p className="text-sm" style={{ color: 'var(--text-3)' }}>Loading…</p>
      ) : attempted.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-2xl mb-2">📭</p>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>No data found.</p>
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16">
          <p className="text-2xl mb-2">🔍</p>
          <p className="text-sm" style={{ color: 'var(--text-3)' }}>No results found</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          {/* Desktop table — difficulty levels as their own columns, same
              table convention as the main History page, so this page reads
              the same way as the rest of the app instead of a one-off layout. */}
          <div className="hidden sm:grid gap-4 px-6 py-3.5 text-xs font-bold uppercase tracking-widest border-b"
               style={{
                 gridTemplateColumns: perExam ? '2fr 1fr 1fr 1fr 1fr' : '1.6fr 1.8fr 1fr 1fr 1fr 1fr',
                 color: 'var(--text-3)', borderColor: 'var(--border)', background: 'var(--surface-2)',
               }}>
            {!perExam && <span>Examinee</span>}
            <span>Exam Name</span>
            {TRIAL_LEVELS_ORDER.map(level => <span key={level} className="text-center">{level}</span>)}
          </div>

          {filtered.map(r => (
            <div key={perExam ? r.examId : `${r.userId}_${r.examId}`} className="border-b last:border-0" style={{ borderColor: 'var(--border)' }}>
              {/* Desktop row */}
              <div className="hidden sm:grid gap-4 px-6 py-3.5 items-center"
                   style={{ gridTemplateColumns: perExam ? '2fr 1fr 1fr 1fr 1fr' : '1.6fr 1.8fr 1fr 1fr 1fr 1fr' }}>
                {!perExam && (
                  <div className="min-w-0">
                    <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{r.userName}</p>
                    {r.userEmail && <p className="text-xs truncate" style={{ color: 'var(--text-3)' }}>{r.userEmail}</p>}
                  </div>
                )}
                <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{r.examName}</p>
                {TRIAL_LEVELS_ORDER.map(level => (
                  <p key={level} className="text-sm font-bold text-center" style={{ color: r.levels[level] > 0 ? 'var(--brand)' : 'var(--text-3)' }}>
                    {r.levels[level]}
                  </p>
                ))}
              </div>
              {/* Mobile row */}
              <div className="sm:hidden px-4 py-3.5">
                {!perExam && <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-1)' }}>{r.userName}</p>}
                <p className="text-xs truncate mb-2" style={{ color: perExam ? 'var(--text-1)' : 'var(--text-3)' }}>{r.examName}</p>
                <div className="grid grid-cols-4 gap-1.5">
                  {TRIAL_LEVELS_ORDER.map(level => (
                    <div key={level} className="text-center px-1.5 py-1 rounded-lg" style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                      <p className="text-sm font-bold" style={{ color: r.levels[level] > 0 ? 'var(--brand)' : 'var(--text-3)' }}>{r.levels[level]}</p>
                      <p className="text-[0.55rem]" style={{ color: 'var(--text-3)' }}>{level}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
